/**
 * AboutThisFoodCard — compact "About This Food" summary that surfaces
 * whichever knowledge sections have data for a given food item.
 *
 * Reused everywhere a product name appears (product cards, list rows,
 * deals, surplus, donation, vendor pages). Renders **only** if we can
 * resolve the item; falls back to a plain-link chip otherwise so the
 * caller never has to fetch first.
 *
 * Props:
 *   slug          — kebab-case food_items slug (required)
 *   variant       — "card" (default) | "chip" (mini badge only)
 *   className     — extra classes for outer wrapper
 *
 * Data source: `GET /api/food-items/{slug}` (cheap, ETag-cacheable).
 * Only reads once per slug per mount — no polling.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Salad, Leaf, HeartPulse, Flame, Archive, Sprout, AlertTriangle, Newspaper, BookOpen, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const has = (v) => v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0) && !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

// Signal chips advertised at a glance. Each row = one section on the
// full FoodItemPage — click → deep-link into that tab.
const SIGNALS = [
  { key: "nutrition",       label: "Nutrition",       Icon: Salad,       tab: "nutrition",    when: (f) => has(f.nutrition) },
  { key: "health",          label: "Health & GI",     Icon: HeartPulse,  tab: "health-gi",    when: (f) => has(f.health_benefits) || has(f.glycemic_index) },
  { key: "prepare",         label: "Prepare & cook", Icon: Flame,        tab: "prepare",      when: (f) => has(f.consumption) || (f.recipes || []).length > 0 },
  { key: "storage",         label: "Storage",         Icon: Archive,     tab: "storage",      when: (f) => has(f.storage) },
  { key: "waste",           label: "Waste-reduce",    Icon: Sprout,      tab: "storage",      when: (f) => has(f.waste_index) || has(f.storage?.waste_reduction) },
  { key: "safety",          label: "Food safety",     Icon: AlertTriangle, tab: "safety",     when: (f) => has(f.food_safety) },
  { key: "news",            label: "Latest news",     Icon: Newspaper,   tab: "news",         when: () => true /* always fetch */ },
  { key: "research",        label: "Research",        Icon: BookOpen,    tab: "research",     when: (f) => (f.references || []).length > 0 || (f.website_links || []).length > 0 },
];

export default function AboutThisFoodCard({ slug, variant = "card", className = "" }) {
  const [f, setF] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/food-items/${slug}`);
        if (!cancelled) setF(r.data);
      } catch (e) {
        if (!cancelled && e.response?.status === 404) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Chip variant — tiny inline badge next to a product name. Always
  // links even before the fetch resolves so the click never feels
  // dead.
  if (variant === "chip") {
    return (
      <Link
        to={`/food-pulse/food/${slug}`}
        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition hover:bg-white ${className}`}
        style={{ borderColor: "#B8D3A1", color: "#2F6B2F", background: "#F0F7EA" }}
        data-testid={`about-food-chip-${slug}`}
      >
        <Leaf className="w-3 h-3" />
        About this food
      </Link>
    );
  }

  // Card variant — full mini-card. Renders during load with skeleton.
  if (notFound) return null;
  const available = f ? SIGNALS.filter((s) => s.when(f)) : [];

  return (
    <div
      className={`rounded-2xl border p-4 bg-white ${className}`}
      style={{ borderColor: "#B8D3A1" }}
      data-testid={`about-food-card-${slug}`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#2F6B2F" }}>About this food</div>
          <div className="font-display text-lg leading-tight" style={{ color: "#1A3B1A" }}>
            {f?.common_name || slug.replace(/-/g, " ")}
          </div>
        </div>
        <Link
          to={`/food-pulse/food/${slug}`}
          className="text-xs font-semibold underline shrink-0"
          style={{ color: "#2F6B2F" }}
          data-testid={`about-food-open-${slug}`}
        >
          Learn <ArrowRight className="inline w-3 h-3 -mt-0.5" />
        </Link>
      </div>

      {(f?.short_description || f?.description) && (
        <p className="text-xs leading-relaxed mb-3 line-clamp-3" style={{ color: "#3E5A3E" }}>
          {f.short_description || f.description}
        </p>
      )}

      {available.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {available.map(({ key, label, Icon, tab }) => (
            <Link
              key={key}
              to={`/food-pulse/food/${slug}?tab=${tab}`}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border hover:bg-white transition"
              style={{ borderColor: "#DCEAD1", background: "#F7FBF4", color: "#2F6B2F" }}
              data-testid={`about-food-signal-${slug}-${key}`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
